import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { app } from "@microsoft/teams-js";
import { TeamRequest, RequestStatus, AppSettings, QuotaModel } from "../types";
import { apiService } from "../services/api.service";
import { telemetryService } from "../services/telemetry.service";

export type RequestStatusFilter = "all" | RequestStatus;

export interface ErrorNotificationState {
  isOpen: boolean;
  title: string;
  message: string;
}

export const useRequestsPage = () => {
  const { t } = useTranslation("requestsPage");

  const [requests, setRequests] = useState<TeamRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [currentUserEmail, setCurrentUserEmail] = useState<string>("");
  const [isApprover, setIsApprover] = useState(false);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [quotaModels, setQuotaModels] = useState<QuotaModel[]>([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<RequestStatusFilter>("all");

  const [selectedRequest, setSelectedRequest] = useState<TeamRequest | null>(null);
  const [approvalAction, setApprovalAction] = useState<"approve" | "reject" | null>(null);
  const [comments, setComments] = useState("");
  const [processingAction, setProcessingAction] = useState<"approve" | "reject" | null>(null);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [errorNotification, setErrorNotification] = useState<ErrorNotificationState>({
    isOpen: false,
    title: "",
    message: ""
  });

  const showErrorNotification = useCallback((title: string, message: string) => {
    setErrorNotification({ isOpen: true, title, message });
  }, []);

  const closeErrorNotification = useCallback(() => {
    setErrorNotification((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const loadRequests = useCallback(async () => {
    try {
      const resp = await apiService.getRequests();
      if (resp.success && resp.data) {
        setRequests(resp.data);
      }
    } catch (err: any) {
      console.error("Error loading requests:", err);
      setError(t("messages.errorLoading"));
    }
  }, [t]);

  const initialize = useCallback(async () => {
    try {
      setLoading(true);

      const context = await app.getContext();
      const userId = context.user?.id || "";
      const userEmail = context.user?.userPrincipalName || context.user?.loginHint || "";
      setCurrentUserId(userId);
      setCurrentUserEmail(userEmail);

      const settingsResp = await apiService.getSettings();
      if (settingsResp.success && settingsResp.data) {
        setSettings(settingsResp.data);
        const approvers = settingsResp.data.approvers || [];
        const userIsApprover = approvers.some((a) => a.id === userId);
        setIsApprover(userIsApprover);
      }

      // Charger les modèles de quota uniquement si la fonctionnalité est active et autorisée pour ce tenant
      try {
        const extResp = await apiService.getExtensionsStatus();
        if (extResp.success && extResp.data) {
          const quotaDetail = extResp.data.featureDetails?.find(f => f.key === 'quota');
          const isQuotaEnabled = quotaDetail ? quotaDetail.enabled : Boolean(extResp.data.features?.quota);
          const isQuotaPremium = quotaDetail?.isPremium ?? false;
          const isPaid = extResp.data.isPaidTier ?? false;
          const canAccessQuota = isQuotaEnabled && (!isQuotaPremium || isPaid);

          if (canAccessQuota) {
            const quotaResp = await apiService.listQuotaModels();
            if (quotaResp.success && quotaResp.data) {
              setQuotaModels(quotaResp.data);
            }
          }
        }
      } catch (quotaErr) {
        console.warn("[RequestsPage] Error checking quota feature or loading quota models:", quotaErr);
      }

      await loadRequests();

      let requestId: string | undefined;

      const urlParams = new URLSearchParams(window.location.search);
      const urlRequestId = urlParams.get("requestId");
      if (urlRequestId) {
        requestId = urlRequestId;
        console.log("[RequestsPage] RequestId found in URL parameters:", requestId);
      }

      if (!requestId && window.location.hash) {
        try {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const hashRequestId = hashParams.get("requestId");
          if (hashRequestId) {
            requestId = hashRequestId;
            console.log("[RequestsPage] RequestId found in URL hash:", requestId);
          }
        } catch (e) {
          console.warn("[RequestsPage] Error parsing URL hash:", e);
        }
      }

      if (!requestId && context.page?.subPageId) {
        requestId = context.page.subPageId;
        console.log("[RequestsPage] RequestId found in Teams context (subPageId):", requestId);
      }

      if (!requestId && context.page?.frameContext) {
        try {
          const frameContext = JSON.parse(context.page.frameContext as any);
          if (frameContext.subEntityId) {
            requestId = frameContext.subEntityId;
            console.log("[RequestsPage] RequestId found in Teams context (frameContext):", requestId);
          }
        } catch (e) {
          console.warn("[RequestsPage] Error parsing Teams frameContext:", e);
        }
      }

      if (requestId && requestId.trim().length > 0) {
        console.log("[RequestsPage] Looking for request with ID:", requestId);
        const resp = await apiService.getRequests();
        if (resp.success && resp.data) {
          const request = resp.data.find((r) => r.id === requestId);
          if (request) {
            console.log("[RequestsPage] Opening modal for request:", request);
            setSelectedRequest(request);
            setApprovalAction(null);
          } else {
            console.warn("[RequestsPage] Request not found with ID:", requestId);
          }
        }
      }
    } catch (err: any) {
      console.error("Error initializing:", err);
      setError(t("messages.errorInitializing"));
    } finally {
      setLoading(false);
    }
  }, [loadRequests, t]);

  const didMountRef = useRef(false);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (didMountRef.current && !loading && currentUserId) {
      telemetryService.capture('requests_filter_changed', currentUserId, {
        properties: {
          filter_value: statusFilter,
        }
      });
    }
    didMountRef.current = true;
  }, [statusFilter]);

  useEffect(() => {
    if (!loading && currentUserId && searchQuery) {
      const timer = setTimeout(() => {
        telemetryService.capture('requests_search_performed', currentUserId, {
          properties: {
            query_length: searchQuery.length,
            status_filter: statusFilter,
            result_count: filteredRequests.length,
          }
        });
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [searchQuery]);

  const filteredRequests = useMemo(() => {
    let filtered = requests;

    if (!isApprover && currentUserId) {
      filtered = filtered.filter((r) => r.requesterId === currentUserId);
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.teamName.toLowerCase().includes(query) ||
          r.requesterEmail.toLowerCase().includes(query) ||
          r.description.toLowerCase().includes(query)
      );
    }

    return filtered.sort(
      (a, b) => new Date(b.submittedDate).getTime() - new Date(a.submittedDate).getTime()
    );
  }, [requests, isApprover, currentUserId, statusFilter, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery]);

  const paginatedData = useMemo(() => {
    const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const items = filteredRequests.slice(startIndex, endIndex);

    return {
      items,
      totalPages,
      totalItems: filteredRequests.length,
      startIndex: filteredRequests.length > 0 ? startIndex + 1 : 0,
      endIndex: Math.min(endIndex, filteredRequests.length)
    };
  }, [filteredRequests, currentPage, itemsPerPage]);

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handleItemsPerPageChange = useCallback((newValue: number) => {
    setItemsPerPage(newValue);
    setCurrentPage(1);
  }, []);

  const handleApprove = useCallback(async () => {
    if (!selectedRequest) return;
    setProcessingAction("approve");
    try {
      const resp = await apiService.approveRequest(
        selectedRequest.id!,
        currentUserId,
        currentUserEmail,
        comments
      );
      if (resp.success) {
        telemetryService.capture('request_approved', currentUserId, {
          properties: {
            request_id: selectedRequest.id || '',
            approval_mode: settings?.notificationSettings?.approvalMode || 'basic',
            has_comments: !!comments.trim(),
            approver_is_admin: false,
          }
        });
        await loadRequests();
        setSelectedRequest(null);
        setApprovalAction(null);
        setComments("");
      } else {
        showErrorNotification(
          t("messages.errorApproving"),
          resp.error || t("messages.errorGeneric")
        );
      }
    } catch (err: any) {
      console.error("Error approving request:", err);
      showErrorNotification(
        t("messages.errorApproving"),
        err?.message || t("messages.errorUnexpected")
      );
    } finally {
      setProcessingAction(null);
    }
  }, [selectedRequest, currentUserId, currentUserEmail, comments, loadRequests, t, showErrorNotification]);

  const handleReject = useCallback(async () => {
    if (!selectedRequest) return;
    setProcessingAction("reject");
    try {
      const resp = await apiService.rejectRequest(
        selectedRequest.id!,
        currentUserId,
        currentUserEmail,
        comments
      );
      if (resp.success) {
        telemetryService.capture('request_rejected', currentUserId, {
          properties: {
            request_id: selectedRequest.id || '',
            approval_mode: settings?.notificationSettings?.approvalMode || 'basic',
            has_comments: !!comments.trim(),
            rejection_reason_length: comments.trim().length,
          }
        });
        await loadRequests();
        setSelectedRequest(null);
        setApprovalAction(null);
        setComments("");
      } else {
        showErrorNotification(
          t("messages.errorRejecting"),
          resp.error || t("messages.errorGeneric")
        );
      }
    } catch (err: any) {
      console.error("Error rejecting request:", err);
      showErrorNotification(
        t("messages.errorRejecting"),
        err?.message || t("messages.errorUnexpected")
      );
    } finally {
      setProcessingAction(null);
    }
  }, [selectedRequest, currentUserId, currentUserEmail, comments, loadRequests, t, showErrorNotification]);

  const handleCancelClick = useCallback((requestId: string) => {
    setRequestToCancel(requestId);
    setCancelDialogOpen(true);
  }, []);

  const handleCancelConfirm = useCallback(async () => {
    if (!requestToCancel) return;
    try {
      const requestToCancelObj = requests.find(r => r.id === requestToCancel);
      const requestAgeHours = requestToCancelObj
        ? Math.round((Date.now() - new Date(requestToCancelObj.submittedDate).getTime()) / (1000 * 60 * 60))
        : 0;
      setProcessingAction("approve");
      const resp = await apiService.cancelRequest(requestToCancel, currentUserId);
      if (resp.success) {
        telemetryService.capture('request_cancelled', currentUserId, {
          properties: {
            request_id: requestToCancel,
            request_age_hours: requestAgeHours,
          }
        });
        await loadRequests();
        setCancelDialogOpen(false);
        setRequestToCancel(null);
      } else {
        setError(
          t("messages.errorCancelling") + ": " + (resp.error || t("messages.errorGeneric"))
        );
      }
    } catch (err: any) {
      console.error("Error canceling request:", err);
      setError(t("messages.errorCancelling"));
    } finally {
      setProcessingAction(null);
    }
  }, [requestToCancel, currentUserId, loadRequests, t]);

  const handleCancelDialogClose = useCallback(() => {
    setCancelDialogOpen(false);
    setRequestToCancel(null);
  }, []);

  const getPrivacyLabel = useCallback(
    (privacy: string) => {
      const labelKeys: Record<string, string> = {
        private: "privacyLabels.private",
        public: "privacyLabels.public"
      };
      return t(labelKeys[privacy] || "privacyLabels." + privacy);
    },
    [t]
  );

  const getApprovalProgress = useCallback(
    (item: TeamRequest) => {
      if (!item.approvalResponses || item.approvalResponses.length === 0) {
        if (item.status === RequestStatus.Approved) {
          return {
            display: t("approvalProgress.approved"),
            tooltip: t("approvalProgress.approvedBy", {
              name: item.approverName || item.approverEmail || "Unknown"
            })
          };
        }
        if (item.status === RequestStatus.Rejected) {
          return {
            display: t("approvalProgress.rejected"),
            tooltip: t("approvalProgress.rejectedBy", {
              name: item.approverName || item.approverEmail || "Unknown"
            })
          };
        }
        return {
          display: t("approvalProgress.noTracking"),
          tooltip: t("approvalProgress.noTrackingTooltip")
        };
      }

      const responses = item.approvalResponses;
      const approvedCount = responses.filter((r) => r.response === "approved").length;
      const rejectedCount = responses.filter((r) => r.response === "rejected").length;
      const pendingCount = responses.filter((r) => r.response === "pending").length;
      const totalCount = responses.length;

      const isBasicAwaitAll = settings?.notificationSettings?.approvalMode === "basicAwaitAll";

      if (!isBasicAwaitAll && totalCount > 0) {
        if (approvedCount > 0) {
          const approver = responses.find((r) => r.response === "approved");
          return {
            display: `✓ ${approver?.approverName || approver?.approverEmail || t("approvalProgress.approved")}`,
            tooltip: t("approvalProgress.approvedBy", {
              name: approver?.approverName || approver?.approverEmail || "Unknown"
            })
          };
        }
        if (rejectedCount > 0) {
          const rejecter = responses.find((r) => r.response === "rejected");
          return {
            display: `✗ ${rejecter?.approverName || rejecter?.approverEmail || t("approvalProgress.rejected")}`,
            tooltip: t("approvalProgress.rejectedBy", {
              name: rejecter?.approverName || rejecter?.approverEmail || "Unknown"
            })
          };
        }
        if (pendingCount > 0) {
          return {
            display: t("approvalProgress.pending"),
            tooltip: t("approvalProgress.pendingTooltip", { count: pendingCount })
          };
        }
      }

      if (approvedCount === totalCount) {
        return {
          display: t("approvalProgress.allApproved", { approved: approvedCount, total: totalCount }),
          tooltip: t("approvalProgress.allApprovedTooltip", { approved: approvedCount, total: totalCount })
        };
      }
      if (rejectedCount > 0) {
        return {
          display: t("approvalProgress.someRejected", { rejected: rejectedCount, total: totalCount }),
          tooltip: t("approvalProgress.someRejectedTooltip", { rejected: rejectedCount, total: totalCount })
        };
      }
      if (approvedCount > 0) {
        const isFinalized =
          item.status === RequestStatus.Approved ||
          item.status === RequestStatus.Completed ||
          item.status === RequestStatus.Created;
        return {
          display: `${isFinalized ? "✓" : "⏳"} ${approvedCount}/${totalCount}`,
          tooltip: isFinalized
            ? t("approvalProgress.partialApprovalTooltip", { approved: approvedCount, pending: pendingCount })
            : t("approvalProgress.partialApprovalWaiting", {
                approved: approvedCount,
                pending: pendingCount,
                total: totalCount
              })
        };
      }
      return {
        display: t("approvalProgress.allPending", { total: totalCount }),
        tooltip: t("approvalProgress.allPendingTooltip", { total: totalCount })
      };
    },
    [settings, t]
  );

  return {
    // State
    requests,
    loading,
    error,
    currentUserId,
    currentUserEmail,
    isApprover,
    settings,
    quotaModels,
    searchQuery,
    statusFilter,
    selectedRequest,
    approvalAction,
    comments,
    processingAction,
    cancelDialogOpen,
    requestToCancel,
    currentPage,
    itemsPerPage,
    errorNotification,
    filteredRequests,
    paginatedData,

    // Setters
    setSearchQuery,
    setStatusFilter,
    setSelectedRequest,
    setApprovalAction,
    setComments,

    // Handlers
    handlePageChange,
    handleItemsPerPageChange,
    handleApprove,
    handleReject,
    handleCancelClick,
    handleCancelConfirm,
    handleCancelDialogClose,
    showErrorNotification,
    closeErrorNotification,
    initialize,
    getPrivacyLabel,
    getApprovalProgress
  };
}
